// ─── THESIS DESK ──────────────────────────────────────────────────────────────
// Fetches the caller's active thesis. No thesis -> the builder. A thesis -> the
// desk. The component decides; App.jsx only routes to it.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { C, s, font, backendFetch, NousIcon } from "./core";
import { ThesisBuilder } from "./components/ThesisBuilder";
import { ThesisShareCard, SHARE_CARD_WIDTH } from "./components/ThesisShareCard";

export const NOTE_MAX = 2000;
// ⚠ The same four questions the builder shows at authorship, rendered above the
// editor rather than as placeholder text that vanishes on the first keystroke.
// ⚠ NOT duplicated from ThesisBuilder by import: the builder is a standalone
// component with its own copy, and a shared constants module does not exist yet.
// If a third editor appears, extract these rather than copying them again.
const NOTE_QUESTIONS = [
  "What is your thesis, in your own words?",
  "What do you see that others don't?",
  "What would prove you right?",
  "What would prove you wrong?",
];

// ═══════════════════ physical world ══════════════════════════════════════════

// ⚠⚠ A DISPLAY-LAYER MAPPING, DERIVED FROM NOTHING. There is no sector/airport
// association anywhere in the backend and this is not inferred from data — it is
// an editorial claim that a corporate jet at Teterboro is more likely to matter
// to a financials thesis than to a semiconductor one. Treat it as a heuristic
// for surfacing context, never as evidence about any ticker.
//
// ⚠ All 12 monitored airports appear at least once. KPBI was absent from the
// original draft; Palm Beach is a finance-relocation hub, so it sits with the
// other financial-centre fields rather than going unmapped.
export const SECTOR_AIRPORTS = {
  Technology: ["KSJC", "KBUR", "KVNY"],
  Semiconductors: ["KSJC", "KADS", "KSDL"],
  Energy: ["KHOU", "KDAL"],
  Financials: ["KTEB", "KHPN", "KBED", "KPBI"],
  Healthcare: ["KBUR", "KOPF"],
  Industrials: ["KDAL", "KHOU"],
  Consumer: ["KVNY", "KBUR"],
  Macro: ["KTEB", "KHPN"],
  "Crypto-adjacent": ["KTEB", "KBUR"],
  Defense: ["KADS", "KDAL"],
};

// ⚠ Duplicated from PhysicalIntelTab.jsx rather than imported: that file is not
// in this change's scope. The 12 codes are fixed by the backend geo-fence, so
// the copies cannot drift without a backend change that would touch both.
export const AIRPORT_NAMES = {
  KTEB: "Teterboro", KHPN: "Westchester", KBED: "Hanscom Field",
  KOPF: "Opa-locka", KPBI: "Palm Beach", KDAL: "Dallas Love",
  KADS: "Addison", KHOU: "Houston Hobby", KVNY: "Van Nuys",
  KSJC: "San Jose", KSDL: "Scottsdale", KBUR: "Burbank",
};

export const SECTOR_COLORS = {
  Technology: C.blue,
  Semiconductors: C.purple,
  Energy: C.orange,
  Financials: C.green,
  // TODO: add to core.jsx palette
  Healthcare: "#FF6B9D",
  Industrials: C.textMid,
  // TODO: add to core.jsx palette
  Consumer: "#FFD700",
  Macro: C.textDim,
  // TODO: add to core.jsx palette — bitcoin orange
  "Crypto-adjacent": "#F7931A",
  Defense: C.red,
};

// ⚠⚠ FRACTIONAL OPERATORS ARE NOT OWNERS. NetJets and the rest hold the
// registration for hundreds of airframes flown by unrelated customers, so
// "NetJets" as faa_owner says only that someone chartered a jet — it identifies
// no company and is not intelligence about any sector. Matched on a normalised
// prefix because the registry spells them inconsistently ("NETJETS SALES INC",
// "NETJETS AVIATION INC").
const FRACTIONAL_OWNERS = [
  "NETJETS", "FLEXJET", "VISTAJET", "JET LINX", "EXECUTIVE JET",
  "WHEELS UP", "NICHOLAS AIR", "PLANESENSE", "AIRSHARE",
];

function isFractional(owner) {
  const o = String(owner || "").trim().toUpperCase();
  if (!o) return false;
  return FRACTIONAL_OWNERS.some((f) => o.startsWith(f));
}

export function airportLabel(code) {
  const c = String(code || "").toUpperCase();
  return AIRPORT_NAMES[c] || c || "—";
}

function flightAgo(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  const secs = Math.max(0, (Date.now() - ms) / 1000);
  if (secs < 3600) return `${Math.max(1, Math.round(secs / 60))}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

// ⚠ Exported so the ticker breakdown can show its aeroplane pip from the same
// filter the panel uses — two filters would let the pip and the panel disagree.
export function relevantFlights(events, sectors) {
  const want = new Map();          // ICAO -> first sector that claims it
  for (const sec of sectors || []) {
    for (const ap of SECTOR_AIRPORTS[sec] || []) {
      if (!want.has(ap)) want.set(ap, sec);
    }
  }
  if (!want.size) return { flights: [], airports: 0 };

  const out = [];
  for (const f of events || []) {
    const dep = String(f.departure_icao || "").toUpperCase();
    const arr = String(f.arrival_icao || "").toUpperCase();
    const depHit = want.has(dep);
    const arrHit = want.has(arr);
    if (!depHit && !arrHit) continue;
    // ⚠ A fractional-only record is dropped, not shown greyed out. It carries
    // no company, so there is nothing for a reader to act on or verify.
    if (!f.faa_owner || isFractional(f.faa_owner)) continue;
    out.push({
      ...f,
      _dep: dep,
      _arr: arr,
      _depHit: depHit,
      _arrHit: arrHit,
      _sector: want.get(depHit ? dep : arr),
      _ts: Date.parse(f.last_updated || "") || 0,
    });
  }
  out.sort((a, b) => b._ts - a._ts);
  return { flights: out, airports: want.size };
}


const PHYS_CAP = 5;
const PHYS_REFRESH_MS = 60_000;
export const PHYS_ANCHOR = "thesis-physical-world";

export function PhysicalWorldPanel({ sectors, endpoint, onNavigate, footer, onSectors }) {
  const [events, setEvents] = useState([]);

  const pull = useCallback(async () => {
    try {
      const r = await backendFetch(endpoint, endpoint);
      if (!r.ok) return;
      const d = await r.json();
      // ⚠ The key is "events" even though these are flight records — the
      // backend kept that name to avoid a silent contract break, and both the
      // authenticated and public routes match.
      setEvents(Array.isArray(d?.events) ? d.events : []);
    } catch { /* an empty feed is a fine outcome */ }
  }, [endpoint]);

  useEffect(() => { pull(); }, [pull]);
  useEffect(() => {
    const id = setInterval(pull, PHYS_REFRESH_MS);
    return () => clearInterval(id);
  }, [pull]);

  const { flights, airports } = relevantFlights(events, sectors);
  const shown = flights.slice(0, PHYS_CAP);

  // ⚠⚠ REPORTED UP FROM THE SAME FILTER THE PANEL RENDERS. The ticker breakdown
  // shows an aeroplane pip for sectors with activity, and computing that from a
  // second filter would let the pip and the panel disagree — a pip on a sector
  // the panel lists nothing for is worse than no pip.
  // ⚠ Keyed on a sorted STRING, not the Set: a fresh Set every render would make
  // this effect fire forever.
  const activeKey = [...new Set(flights.map((f) => f._sector))].sort().join(",");
  useEffect(() => {
    if (typeof onSectors !== "function") return;
    onSectors(new Set(activeKey ? activeKey.split(",") : []));
  }, [activeKey, onSectors]);

  return (
    <div id={PHYS_ANCHOR}>
      <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em",
                    margin: "18px 0 2px" }}>
        PHYSICAL WORLD INTELLIGENCE
      </div>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>
        Flight activity relevant to your thesis sectors
      </div>

      {shown.length === 0 ? (
        <div style={{ ...s.panel, padding: 14, color: C.textDim, lineHeight: 1.7 }}>
          No relevant flight activity detected in the last hour.
          {airports > 0 && <> Watching {airports} airport{airports === 1 ? "" : "s"} across your thesis sectors.</>}
        </div>
      ) : shown.map((f, i) => {
        const col = SECTOR_COLORS[f._sector] || C.textDim;
        const both = f._depHit && f._arrHit;
        return (
          <div key={f.callsign || i} style={{
            ...s.panel, padding: 10, marginBottom: 6,
            display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 700 }}>
                {f.faa_owner || f.registration || f.callsign}
              </div>
              {(f.faa_make || f.faa_model) && (
                <div style={{ fontSize: 11, color: C.textDim }}>
                  {[f.faa_make, f.faa_model].filter(Boolean).join(" ")}
                </div>
              )}
              <div style={{ fontSize: 11, color: C.textMid, marginTop: 3 }}>
                {both ? (
                  `${airportLabel(f._dep)} → ${airportLabel(f._arr)}`
                ) : f._depHit ? (
                  <>DEPARTED {airportLabel(f._dep)}</>
                ) : (
                  <>ARRIVED {airportLabel(f._arr)}</>
                )}
              </div>
              <div style={{ fontSize: 10, color: C.textDim }}>
                {flightAgo(f.last_updated)}
              </div>
            </div>
            <span style={{
              fontSize: 9.5, fontWeight: 700, color: col,
              border: `1px solid ${col}`, borderRadius: 10, padding: "2px 8px",
              whiteSpace: "nowrap", letterSpacing: "0.06em",
            }}>
              {String(f._sector || "").toUpperCase()}
            </span>
          </div>
        );
      })}

      {footer}
      {/* ⚠ Rendered only when a navigator exists — the same guard the chart-jump
          button uses. On the public ledger there is no tab shell to navigate. */}
      {typeof onNavigate === "function" && (
        <button style={{ ...s.btn(), minHeight: 40, marginTop: 6 }}
                onClick={() => onNavigate("PHYSICAL INTEL")}>
          VIEW ALL FLIGHTS →
        </button>
      )}
    </div>
  );
}

const SECTOR_ICONS = {
  Technology: "💻", Semiconductors: "⚡", Energy: "🛢", Financials: "🏦",
  Healthcare: "🧬", Industrials: "⚙️", Consumer: "🛍", Macro: "🌐",
  "Crypto-adjacent": "₿", Defense: "🛡",
};

// ⚠ WATCHING is grey on purpose. It is the absence of evidence, and colouring
// it would imply a reading the platform has not made.
const SCORE_COLOR = {
  "STRONGLY ALIGNED": C.green,
  ALIGNED: C.green,
  MIXED: C.orange,
  CHALLENGED: C.red,
  "STRONGLY CHALLENGED": C.red,
  WATCHING: C.textDim,
};

const ALIGN_STYLE = {
  ALIGNS: { icon: "✅", color: C.green, label: "ALIGNS" },
  CHALLENGES: { icon: "❌", color: C.red, label: "CHALLENGES" },
  NEUTRAL: { icon: "➖", color: C.textDim, label: "NEUTRAL" },
};

const WEIGHT_COLOR = { high: C.green, medium: C.blue, low: C.textDim };

const ET = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York", month: "short", day: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: false,
});

// ⚠ "2h ago" reads faster than a timestamp in a breakdown card, where the
// question is recency rather than when exactly.
function ago(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  const secs = Math.max(0, (Date.now() - ms) / 1000);
  if (secs < 3600) return `${Math.max(1, Math.round(secs / 60))}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function etStamp(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "—";
  try { return `${ET.format(new Date(ms))} ET`; } catch { return "—"; }
}

// ⚠ Cleared server-side on read, so the dot disappears after one look. The
// outcomes themselves stay on the desk — the bell is a nudge, not the record.
function NotificationBell({ items, open, onToggle, onShare }) {
  // ⚠ Unread, not merely present. Notifications now persist after being read,
  // so "any item exists" would light the dot forever.
  const unread = items.filter((n) => !n.read);
  const shareable = unread.some((n) => n.prompt_share);
  const has = unread.length > 0;
  return (
    <div style={{ position: "relative" }}>
      {/* ⚠ A keyframe cannot be expressed as an inline style, so the pulse
          needs a real stylesheet rule. Scoped to one class name and injected
          once per bell — harmless if two bells ever mount. */}
      <style>{`@keyframes qsPulse {
        0%,100% { opacity: 1; transform: scale(1); }
        50%     { opacity: 0.35; transform: scale(1.45); }
      }`}</style>
      <button
        onClick={onToggle}
        aria-label={has ? `${unread.length} unread notifications` : "Notifications"}
        style={{ ...s.btn(), minHeight: 44, position: "relative" }}
      >
        {"\u{1F514}"}
        {has && (
          // ⚠⚠ GREEN OVERRIDES RED, and it pulses. Red says "something
          // resolved"; green says "something worth showing people resolved".
          // Collapsing them would bury the one notification the whole reshare
          // mechanic exists to surface.
          <span style={{
            position: "absolute", top: 6, right: 6, width: 7, height: 7,
            borderRadius: "50%",
            background: shareable ? C.green : C.red,
            animation: shareable ? "qsPulse 1.6s ease-in-out infinite" : undefined,
          }} />
        )}
      </button>
      {open && (
        <div style={{
          position: "absolute", right: 0, top: "100%", marginTop: 4, zIndex: 20,
          width: 280, maxHeight: 320, overflowY: "auto",
          background: C.panel, border: `1px solid ${C.border}`, borderRadius: 3,
          padding: 8,
        }}>
          {!has && (
            <div style={{ color: C.textDim, fontSize: 11 }}>No new outcomes.</div>
          )}
          {items.map((nt, i) => {
            const ok = nt.direction_correct;
            const col = ok === true ? C.green : ok === false ? C.red : C.textMid;
            const pct = typeof nt.outcome_pct === "number"
              ? `${nt.outcome_pct > 0 ? "+" : ""}${nt.outcome_pct.toFixed(1)}%` : "\u2014";
            return (
              <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 12 }}>
                  <strong>{nt.ticker}</strong>{" "}
                  <span style={{ color: C.textDim }}>{nt.window} outcome:</span>{" "}
                  <span style={{ color: col }}>{pct}</span>
                </div>
                <div style={{ fontSize: 11, color: C.textDim }}>
                  {nt.alignment === "ALIGNS" ? "Aligns with"
                    : nt.alignment === "CHALLENGES" ? "Challenges" : "Neutral to"} your thesis.
                </div>
                {/* ⚠ The reshare moment. The platform noticed the outcome; the
                    button is offered at the instant it resolved rather than
                    waiting for the user to come looking. */}
                {nt.prompt_share && onShare && (
                  <button style={{ ...s.btn("green"), padding: "2px 8px", minHeight: 32,
                                   marginTop: 4 }}
                          onClick={() => onShare(nt)}>
                    SHARE THIS WIN →
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Disclaimer() {
  return (
    <div style={{ fontSize: 10, color: C.textDim, marginTop: 14, letterSpacing: "0.04em" }}>
      Signal intelligence only · Not investment advice
    </div>
  );
}

// ⚠ Exported for src/pages/PublicLedger.jsx. A public ledger that rendered its
// own copy of this row would drift from the desk the first time either changed,
// and the whole claim of the page is that it shows the same record the author
// sees.
export function SignalRow({ sig, windows }) {
  const a = ALIGN_STYLE[String(sig.thesis_alignment || "").toUpperCase()] || ALIGN_STYLE.NEUTRAL;
  const wc = WEIGHT_COLOR[String(sig.signal_weight || "").toLowerCase()] || C.textDim;
  return (
    <div style={{
      ...s.panel, padding: 10, marginBottom: 6,
      display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap",
    }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: a.color, border: `1px solid ${a.color}`,
        borderRadius: 10, padding: "2px 8px", whiteSpace: "nowrap",
      }}>
        {a.icon} {a.label}
      </span>
      <div style={{ flex: 1, minWidth: 180 }}>
        <div>
          <strong>{sig.ticker}</strong>
          <span style={{ color: C.textDim }}> · {sig.signal_label}</span>
          <span title={`${sig.signal_weight} priority`}
                style={{ color: wc, marginLeft: 6 }}>●</span>
        </div>
        <div style={{ fontSize: 11, color: C.textDim }}>
          {etStamp(sig.detected_at)}
          {sig.price_at_detection != null
            ? ` · $${Number(sig.price_at_detection).toFixed(2)}`
            : ""}
        </div>
      </div>
      {/* ⚠ Only the windows belonging to THIS thesis's timeframe are rendered.
          Every other outcome column is NULL by design and showing it would
          read as a measurement that came back empty. */}
      <div style={{ fontSize: 11, color: C.textDim, textAlign: "right", minWidth: 96 }}>
        {windows.map((w) => {
          const v = sig[`outcome_${w}`];
          const ok = sig[`direction_correct_${w}`];
          if (v == null) return <div key={w}>{w}: pending</div>;
          const n = Number(v);
          return (
            <div key={w} style={{ color: ok ? C.green : C.red }}>
              {w}: {n > 0 ? "+" : ""}{n.toFixed(1)}% {ok ? "✓" : "✗"}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function noteDate(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric",
    }).format(new Date(ms));
  } catch { return ""; }
}

// ⚠⚠ THE SEAL IS THE FEATURE. A locked note is not a disabled input — it is the
// evidence that the thinking predates the outcomes beside it. Every string here
// is written to say that, because a UI that apologises for the lock teaches the
// user to read it as breakage.
function NoteSection({ thesis, onSaved }) {
  const locked = !!thesis.note_locked_at;
  const note = thesis.thesis_note || "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    const text = draft.slice(0, NOTE_MAX);
    try {
      const path = `/api/thesis/${thesis.id}/note`;
      const r = await backendFetch(path, path, {
        method: "PATCH", body: JSON.stringify({ note: text }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        // ⚠ 403 means the monitor sealed the note while this editor was open.
        // Reload rather than retry — the server state is now the truth.
        setErr(d?.detail || (r.status === 403
          ? "Your note was sealed while you were writing."
          : "Could not save your note."));
        if (r.status === 403) onSaved();
        return;
      }
      const updated = await r.json();
      setEditing(false);
      onSaved(updated);
    } catch {
      setErr("Could not reach the platform.");
    } finally {
      setBusy(false);
    }
  };

  const counterColor = draft.length >= NOTE_MAX ? C.red
    : draft.length >= 1800 ? C.orange : C.textDim;

  const label = (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em" }}>
        THESIS NOTE
      </span>
      {locked && (
        <span title={`Sealed ${noteDate(thesis.note_locked_at)} — your original thinking is preserved.`}
              style={{ fontSize: 11, color: C.green }}>
          🔒
        </span>
      )}
    </div>
  );

  // ── sealed ──
  if (locked && note) {
    return (
      <div style={{ marginTop: 18 }}>
        {label}
        <div style={{ ...s.panel, padding: 14, marginTop: 8, whiteSpace: "pre-wrap",
                      lineHeight: 1.7 }}>
          {note}
          <div style={{ fontSize: 10, color: C.green, marginTop: 10,
                        letterSpacing: "0.06em" }}>
            SEALED AT AUTHORSHIP · {noteDate(thesis.note_locked_at)}
          </div>
        </div>
      </div>
    );
  }

  // ⚠ Sealed with nothing written. The window has closed, so the prompt below
  // would be an invitation to do something the server will refuse.
  if (locked) {
    return (
      <div style={{ marginTop: 18 }}>
        {label}
        <div style={{ ...s.panel, padding: 14, marginTop: 8, color: C.textDim }}>
          No note was written before the first signal fired.
        </div>
      </div>
    );
  }

  // ── editable ──
  if (editing || (!note && editing)) {
    return (
      <div style={{ marginTop: 18 }}>
        {label}
        {/* ⚠ Same prompt as the builder's step 6. This editor shipped with NO
            guidance at all — a bare box under a "THESIS NOTE" label — so a user
            editing here had no idea the note was meant to answer anything. */}
        <div style={{ ...s.panel, padding: 10, marginTop: 8 }}>
          {NOTE_QUESTIONS.map((q) => (
            <div key={q} style={{ display: "flex", gap: 8, marginTop: 4,
                                  fontSize: 12, color: C.textDim, lineHeight: 1.6 }}>
              <span style={{ color: C.green }}>·</span>
              <span>{q}</span>
            </div>
          ))}
        </div>
        <textarea
          value={draft} autoFocus
          onChange={(e) => setDraft(e.target.value.slice(0, NOTE_MAX))}
          style={{
            ...s.panel, width: "100%", minHeight: 150, marginTop: 8, padding: 12,
            fontFamily: font, fontSize: 13, color: C.text, lineHeight: 1.7,
            resize: "vertical", boxSizing: "border-box",
          }}
        />
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ ...s.btn("green"), minHeight: 44 }} disabled={busy}
                    onClick={save}>
              {busy ? "…" : "SAVE"}
            </button>
            <button style={{ ...s.btn(), minHeight: 44 }} disabled={busy}
                    onClick={() => { setDraft(note); setEditing(false); setErr(null); }}>
              CANCEL
            </button>
          </div>
          <span style={{ fontSize: 11, color: counterColor }}>
            {draft.length} / {NOTE_MAX}
          </span>
        </div>
        {err && <div style={{ color: C.red, fontSize: 12, marginTop: 6 }}>{err}</div>}
      </div>
    );
  }

  // ── written, still open ──
  if (note) {
    return (
      <div style={{ marginTop: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center" }}>
          {label}
          <button style={{ ...s.btn(), minHeight: 36, padding: "2px 10px" }}
                  onClick={() => { setDraft(note); setEditing(true); }}>
            EDIT
          </button>
        </div>
        <div style={{ ...s.panel, padding: 14, marginTop: 8, whiteSpace: "pre-wrap",
                      lineHeight: 1.7 }}>
          {note}
        </div>
        <div style={{ fontSize: 10, color: C.textDim, marginTop: 6 }}>
          Your note locks permanently when your first signal fires.
        </div>
      </div>
    );
  }

  // ── nothing written yet ──
  return (
    <div style={{ marginTop: 18 }}>
      {label}
      <div style={{ ...s.panel, padding: 14, marginTop: 8, lineHeight: 1.7 }}>
        <div style={{ color: C.textDim }}>
          Explain your thinking before your first signal fires. Once locked, it
          becomes the permanent record of your thesis.
        </div>
        <button style={{ ...s.btn("green"), minHeight: 44, marginTop: 10 }}
                onClick={() => { setDraft(""); setEditing(true); }}>
          ADD NOTE →
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════ share ═══════════════════════════════════════════════

const SHARE_TARGETS = [
  { key: "x", label: "X",
    url: (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}` },
  { key: "li", label: "LinkedIn",
    url: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${u}` },
  { key: "fb", label: "Facebook",
    url: (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
];

// ═══════════════════ intelligence brief ══════════════════════════════════════

/** The brief: a generated transcript, rendered.
 *
 *  ⚠⚠ THE AUDIO LAYER IS GONE. Kokoro synthesis and playback lived here through
 *  four architectures — one call, chunked serial, one-ahead pipeline, and
 *  synthesize-all-first — and were removed in favour of the text. The transcript
 *  was never gated on the audio anyway: Kokoro is WebGPU-only, so on every
 *  iPhone and most Android browsers there was never a player and this is what
 *  those readers already saw.
 *  ⚠ The LOADING state for the brief belongs to the parent, not here. `data` is
 *  the resolved response — this component does not render until briefData
 *  exists, and `briefLoading` drives the button's own "PREPARING BRIEF…" label.
 *  Nothing about generation is decided in this file's audio path any more,
 *  because there is no audio path.
 */
function BriefPanel({ data, onShare }) {
  return (
    <div style={{ ...s.panel, padding: 14, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8,
                    flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.14em", color: C.green,
                      fontWeight: 700 }}>
          INTELLIGENCE BRIEF
        </div>
        <div style={{ fontSize: 10, color: C.textDim }}>
          {etStamp(data.generated_at)}
          {data.cached ? " · cached" : ""}
        </div>
      </div>

      <div style={{ marginTop: 12, paddingLeft: 12, borderLeft: `2px solid ${C.border}`,
                    color: C.text, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
        {data.transcript}
      </div>

      {data.is_shareable && (
        <button style={{ ...s.btn(), minHeight: 44, marginTop: 12, color: C.green }}
                onClick={onShare}>
          Share this brief →
        </button>
      )}
    </div>
  );
}

function ShareModal({ thesis, highlight, pullQuote, onClose, onPublished }) {
  const [card, setCard] = useState(null);
  const [handle, setHandle] = useState(thesis.public_handle || "");
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pngState, setPngState] = useState("idle");
  const exportRef = useRef(null);

  const published = !!(thesis.is_public && thesis.public_handle);
  const publicUrl = published
    ? `https://quant-signal.ai/ledger/@${thesis.public_handle}` : null;

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const path = `/api/thesis/${thesis.id}/card`;
        const r = await backendFetch(path, path);
        if (!r.ok || dead) return;
        const d = await r.json();
        if (!dead) setCard(d);
      } catch { /* the modal still works without a preview */ }
    })();
    return () => { dead = true; };
  }, [thesis.id]);

  const check = async () => {
    const h = handle.replace(/^@/, "").trim();
    if (!h) { setAvail(null); return; }
    try {
      const p = `/api/thesis/handle-available?q=${encodeURIComponent(h)}&thesis_id=${thesis.id}`;
      const r = await backendFetch(p, p);
      setAvail(r.ok ? await r.json() : null);
    } catch { setAvail(null); }
  };

  const publish = async () => {
    setBusy(true);
    setErr(null);
    try {
      const p = `/api/thesis/${thesis.id}/publish`;
      const r = await backendFetch(p, p, {
        method: "POST",
        body: JSON.stringify({ public_handle: handle.replace(/^@/, "").trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.detail || "Could not publish."); return; }
      onPublished(d.thesis);
    } catch {
      setErr("Could not reach the platform.");
    } finally {
      setBusy(false);
    }
  };

  // ⚠⚠ html2canvas IS IMPORTED HERE AND NOWHERE ELSE. A static import would put
  // ~200KB into the entry chunk for a feature most sessions never open. The
  // dynamic import is what keeps it off page init.
  const savePng = async () => {
    if (!exportRef.current) return;
    setPngState("working");
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: "#0B0E14", scale: 2, logging: false, useCORS: true,
      });
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) { setPngState("failed"); return; }
      const file = new File([blob], "thesis-card.png", { type: "image/png" });
      // ⚠ Native sheet when it can carry a FILE. navigator.share exists on
      // desktop Chrome but rejects files, so canShare is checked separately —
      // feature-detecting only share() would throw on send.
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: thesis.thesis_name,
                                url: publicUrl || undefined });
        setPngState("idle");
        return;
      }
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "thesis-card.png";
      a.click();
      URL.revokeObjectURL(href);
      setPngState("idle");
    } catch {
      // ⚠ The card is a bonus; the URL is the share. Failing to rasterise must
      // not present as the share itself having failed.
      setPngState("failed");
    }
  };

  const copy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { setErr("Could not copy. Select the link above instead."); }
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200,
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      padding: 16, overflowY: "auto",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        ...s.panel, padding: 18, maxWidth: 420, width: "100%", marginTop: 24,
        fontFamily: font, color: C.text, fontSize: 13,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: C.green,
                         letterSpacing: "0.08em" }}>
            {published ? "SHARE YOUR LEDGER" : "MAKE YOUR LEDGER PUBLIC"}
          </span>
          <button style={{ ...s.btn(), minHeight: 36 }} onClick={onClose}>✕</button>
        </div>

        {!published && (
          <>
            <div style={{ color: C.textDim, marginBottom: 8 }}>
              Choose a handle for your public ledger:
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: C.textMid }}>@</span>
              <input
                value={handle}
                onChange={(e) => { setHandle(e.target.value); setAvail(null); }}
                onBlur={check}
                placeholder="your_handle"
                style={{
                  ...s.panel, flex: 1, padding: "8px 10px", fontFamily: font,
                  fontSize: 13, color: C.text, minHeight: 44, boxSizing: "border-box",
                }}
              />
            </div>
            {avail && (
              <div style={{ fontSize: 11, marginTop: 5,
                            color: avail.available ? C.green : C.red }}>
                {avail.available ? `@${avail.handle} is available.` : avail.reason}
              </div>
            )}
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 8 }}>
              Your public ledger will be at:<br />
              <span style={{ color: C.textMid }}>
                quant-signal.ai/ledger/@{handle.replace(/^@/, "") || "your_handle"}
              </span>
            </div>
          </>
        )}

        {published && (
          <div style={{ fontSize: 12, marginBottom: 10 }}>
            <div style={{ color: C.textDim, marginBottom: 4 }}>Your public ledger:</div>
            <div style={{ color: C.green, wordBreak: "break-all" }}>{publicUrl}</div>
          </div>
        )}

        {/* ── preview: the real card, scaled, not a mock of it ── */}
        {card && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.16em", color: C.textDim,
                          marginBottom: 6 }}>
              PREVIEW
            </div>
            {/* ⚠ The card zooms rather than transforms, so the wrapper needs
                no height math — the scaled layout box is the real height. */}
            <div style={{ overflow: "hidden" }}>
              <ThesisShareCard card={card} highlight={highlight}
                               pull_quote={pullQuote} scale={0.6} />
            </div>
          </div>
        )}

        {err && <div style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
          {!published && (
            <button style={{ ...s.btn("green"), minHeight: 44 }} disabled={busy}
                    onClick={publish}>
              {busy ? "…" : "MAKE PUBLIC"}
            </button>
          )}
          {published && (
            <>
              {SHARE_TARGETS.map((t) => (
                <button key={t.key} style={{ ...s.btn(), minHeight: 44 }}
                        onClick={() => window.open(
                          t.url(encodeURIComponent(publicUrl),
                                encodeURIComponent(thesis.thesis_name)),
                          "_blank", "noopener,noreferrer")}>
                  {t.label}
                </button>
              ))}
              <button style={{ ...s.btn(), minHeight: 44 }} onClick={copy}>
                {copied ? "COPIED" : "COPY LINK"}
              </button>
              <button style={{ ...s.btn("green"), minHeight: 44 }} onClick={savePng}
                      disabled={pngState === "working" || !card}>
                {pngState === "working" ? "…"
                  : pngState === "failed" ? "IMAGE FAILED — LINK STILL WORKS"
                  : "SAVE CARD IMAGE"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ⚠⚠ THE EXPORT COPY — FULL SIZE, OFF SCREEN, NEVER SCALED. html2canvas
          rasterises real layout, so capturing the scaled preview would produce a
          360px image. Positioned off-viewport rather than display:none, because
          a hidden element has no layout to rasterise at all. */}
      {card && (
        <div aria-hidden="true" style={{
          position: "fixed", left: -99999, top: 0, width: SHARE_CARD_WIDTH,
          pointerEvents: "none",
        }}>
          <div ref={exportRef}>
            <ThesisShareCard card={card} highlight={highlight} pull_quote={pullQuote} />
          </div>
        </div>
      )}
    </div>
  );
}

const DIRECTIONS = ["bullish", "bearish", "contrarian"];
const STYLES = ["growth", "value", "income", "momentum"];
const TIMEFRAMES = ["days", "weeks", "months", "quarters"];
const WEIGHT_STEPS = ["high", "medium", "low"];

function Pick({ options, value, onPick }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => (
        <button key={o} onClick={() => onPick(o)}
                style={{ ...s.btn(value === o ? "green" : undefined), minHeight: 40,
                         opacity: value === o ? 1 : 0.65 }}>
          {o.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

// ⚠⚠ EDIT IS REFUSED ONCE A SIGNAL HAS FIRED, and the refusal is the point:
// editing the thesis would rewrite the question every recorded alignment was an
// answer to. The service enforces it too — this only stops the UI offering what
// the backend will reject.
function EditThesis({ thesis, onCancel, onSaved }) {
  const [name, setName] = useState(thesis.thesis_name || "");
  const [direction, setDirection] = useState(thesis.direction || "");
  const [style, setStyle] = useState(thesis.style || "");
  const [timeframe, setTimeframe] = useState(thesis.timeframe || "");
  const [weights, setWeights] = useState(() => ({ ...(thesis.signal_weights || {}) }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const path = `/api/thesis/${thesis.id}`;
      const r = await backendFetch(path, path, {
        method: "PUT",
        body: JSON.stringify({
          thesis_name: name.trim(),
          direction, style, timeframe,
          // ⚠ Sectors and tickers are sent back UNCHANGED. PUT replaces the
          // whole row, so omitting them would wipe the basket; changing the
          // basket is a rebuild, not an edit.
          sectors: thesis.sectors || [],
          tickers: thesis.tickers || [],
          signal_weights: weights,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d?.detail || "Could not save your changes."); return; }
      onSaved(d);
    } catch {
      setErr("Could not reach the platform.");
    } finally {
      setBusy(false);
    }
  };

  const row = { marginTop: 12 };
  const lab = { fontSize: 11, color: C.textMid, letterSpacing: "0.1em", marginBottom: 4 };

  return (
    <div style={{ ...s.panel, padding: 16 }}>
      <div style={{ fontSize: 11, color: C.textDim, letterSpacing: "0.12em" }}>
        EDIT THESIS
      </div>
      <div style={row}>
        <div style={lab}>NAME</div>
        <input value={name} onChange={(e) => setName(e.target.value.slice(0, 50))}
               style={{ ...s.panel, width: "100%", padding: "8px 10px", minHeight: 44,
                        fontFamily: font, fontSize: 13, color: C.text,
                        boxSizing: "border-box" }} />
      </div>
      <div style={row}><div style={lab}>DIRECTION</div>
        <Pick options={DIRECTIONS} value={direction} onPick={setDirection} /></div>
      <div style={row}><div style={lab}>STYLE</div>
        <Pick options={STYLES} value={style} onPick={setStyle} /></div>
      <div style={row}><div style={lab}>TIMEFRAME</div>
        <Pick options={TIMEFRAMES} value={timeframe} onPick={setTimeframe} /></div>

      <div style={row}>
        <div style={lab}>SIGNAL PRIORITIES</div>
        {Object.keys(weights).sort().map((k) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 8,
                                marginBottom: 4, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, color: C.textDim, minWidth: 150 }}>
              {k.replace(/_/g, " ")}
            </span>
            <Pick options={WEIGHT_STEPS} value={weights[k]}
                  onPick={(v) => setWeights((w) => ({ ...w, [k]: v }))} />
          </div>
        ))}
      </div>

      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button style={{ ...s.btn("green"), minHeight: 44 }} disabled={busy} onClick={save}>
          {busy ? "SAVING…" : "SAVE CHANGES"}
        </button>
        <button style={{ ...s.btn(), minHeight: 44 }} disabled={busy} onClick={onCancel}>
          CANCEL
        </button>
      </div>
    </div>
  );
}

// ═══════════════════ archived theses ═════════════════════════════════════════

function HistoryCard({ item }) {
  const [full, setFull] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const open = async () => {
    if (full) { setFull(null); return; }
    setBusy(true);
    setErr(null);
    try {
      const p = `/api/thesis/${item.id}/archived`;
      const r = await backendFetch(p, p);
      if (!r.ok) { setErr("Could not load this thesis."); return; }
      setFull(await r.json());
    } catch {
      setErr("Could not reach the platform.");
    } finally {
      setBusy(false);
    }
  };

  const scored = item.scored_7d || 0;
  return (
    <div style={{ ...s.panel, padding: 12, marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8,
                    alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700 }}>{item.thesis_name}</div>
          <div style={{ fontSize: 11, color: C.textDim }}>
            {String(item.direction || "").toUpperCase()} ·{" "}
            {String(item.style || "").toUpperCase()} ·{" "}
            {String(item.timeframe || "").toUpperCase()}
          </div>
          <div style={{ fontSize: 10, color: C.textDim }}>
            Created {noteDate(item.created_at)} → Archived {noteDate(item.updated_at)}
          </div>
        </div>
        <button style={{ ...s.btn(), minHeight: 40 }} disabled={busy} onClick={open}>
          {busy ? "…" : full ? "COLLAPSE ↑" : "VIEW FULL →"}
        </button>
      </div>

      <div style={{ fontSize: 11, marginTop: 6 }}>
        <span style={{ color: C.text }}>{item.total_signals}</span>
        <span style={{ color: C.textDim }}> signals · </span>
        <span style={{ color: C.green }}>{item.aligning}</span>
        <span style={{ color: C.textDim }}> aligning · </span>
        <span style={{ color: C.red }}>{item.challenging}</span>
        <span style={{ color: C.textDim }}> challenging</span>
        {/* ⚠ Reported against SCORED signals, never against the total. Most
            signals have a null outcome because their window has not closed, and
            "3 of 200 correct" would read as a catastrophic thesis when it means
            197 are still pending. */}
        {scored > 0 && (
          <span style={{ color: C.textDim }}>
            {" · 7d: "}
            <span style={{ color: C.text }}>{item.correct_7d} of {scored}</span>
            {" correct"}
          </span>
        )}
      </div>

      {item.note_excerpt && (
        <div style={{ fontStyle: "italic", color: C.textDim, fontSize: 12,
                      marginTop: 8, lineHeight: 1.6 }}>
          {item.note_excerpt}
        </div>
      )}

      {err && <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{err}</div>}

      {full && (
        <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
          <div style={{ fontSize: 10, color: C.textDim, letterSpacing: "0.1em",
                        marginBottom: 8 }}>
            ARCHIVED — {noteDate(full.thesis?.updated_at)}
          </div>
          {full.thesis?.thesis_note && (
            <div style={{ ...s.panel, padding: 12, marginBottom: 10,
                          whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
              {full.thesis.thesis_note}
              {full.thesis.note_locked_at && (
                <div style={{ fontSize: 10, color: C.green, marginTop: 8,
                              letterSpacing: "0.06em" }}>
                  SEALED {noteDate(full.thesis.note_locked_at)}
                </div>
              )}
            </div>
          )}
          {/* ⚠⚠ These outcomes can be MORE complete than at archive time — the
              monitor keeps filling windows for archived theses, because what the
              market did after a thesis was retired is still the answer to the
              question it asked. This is the final truth, not a snapshot. */}
          {(full.signals || []).length === 0 ? (
            <div style={{ color: C.textDim, fontSize: 12 }}>
              No signals fired against this thesis.
            </div>
          ) : (full.signals || []).map((sg) => (
            <SignalRow key={sg.id || `${sg.ticker}-${sg.detected_at}`}
                       sig={sg} windows={full.windows || []} />
          ))}
        </div>
      )}
    </div>
  );
}

function ThesisHistory() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await backendFetch("/api/thesis/history", "/api/thesis/history");
        if (!r.ok || dead) return;
        const d = await r.json();
        if (!dead) setItems(Array.isArray(d?.history) ? d.history : []);
      } catch { /* absence of history is not an error */ }
    })();
    return () => { dead = true; };
  }, []);

  // ⚠ No empty state. A user who has never archived anything should not be told
  // so — the section simply is not there, and the builder above is undisturbed.
  if (!items || items.length === 0) return null;

  return (
    <div style={{ marginTop: 26 }}>
      <button onClick={() => setOpen((v) => !v)}
              style={{ ...s.btn(), minHeight: 40, letterSpacing: "0.1em" }}>
        {open ? "▾" : "▸"} PREVIOUS THESES ({items.length})
      </button>
      {open && (
        <div style={{ marginTop: 10 }}>
          {items.map((it) => <HistoryCard key={it.id} item={it} />)}
        </div>
      )}
    </div>
  );
}

// ⚠⚠ A LOCAL BOUNDARY AROUND THE BUILDER, NOT A REPLACEMENT FOR TabBoundary.
// App.jsx already wraps this whole tab, but a throw there replaces the entire
// desk — including the history below the builder — with one error card. This
// keeps the failure where it happened and SHOWS THE MESSAGE, because the
// reported symptom was a blank screen and a silent boundary is how a blank
// screen happens.
class BuilderBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ ...s.panel, padding: 16, color: C.red, lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            The thesis builder failed to load.
          </div>
          <div style={{ color: C.textDim, fontSize: 12 }}>
            {String(this.state.error?.message || this.state.error)}
          </div>
          <button style={{ ...s.btn(), minHeight: 44, marginTop: 10 }}
                  onClick={() => this.setState({ error: null })}>
            RETRY
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ⚠ onNavigate is no longer read here — its only consumer was the
// PhysicalWorldPanel render, which this change removed from the desk. The prop
// stays on the signature because App.jsx still passes it and dropping it would
// mean editing a second file for no gain; PublicLedger's copy of the panel
// still uses it.
export function ThesisDesk({ onNavigate }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [bellOpen, setBellOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareSignal, setShareSignal] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editErr, setEditErr] = useState(null);
  const rootRef = useRef(null);
  // ⚠ Local and deliberately NOT persisted. The desk should open the same way
  // every time — the whole basket visible, every ticker closed — rather than
  // restoring whatever happened to be expanded days ago.
  const [openTickers, setOpenTickers] = useState(() => new Set());
  const [actionError, setActionError] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefData, setBriefData] = useState(null);
  const [briefError, setBriefError] = useState(null);
  const [shareQuote, setShareQuote] = useState(null);

  // ⚠⚠ CLEARS briefData BEFORE the request, not after it resolves. Leaving the
  // old brief on screen during a re-brief would let the user read yesterday's
  // text under a "preparing" button and, worse, keep BriefPanel mounted on the
  // old transcript so the audio effect never re-runs for the new one.
  const requestBrief = useCallback(async () => {
    setBriefLoading(true);
    setBriefError(null);
    setBriefData(null);
    try {
      const p = "/api/thesis/brief";
      const r = await backendFetch(p, p, { method: "POST" });
      if (!r.ok) {
        let msg = "The brief could not be generated. Try again.";
        try { const e = await r.json(); if (e?.detail) msg = String(e.detail); } catch { /* keep */ }
        setBriefError(msg);
        return;
      }
      setBriefData(await r.json());
    } catch {
      setBriefError("Could not reach the platform.");
    } finally {
      setBriefLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await backendFetch("/api/thesis/me", "/api/thesis/me");
      if (r.status === 404) { setState({ loading: false, data: null, error: null }); return; }
      if (!r.ok) { setState({ loading: false, data: null, error: "unreachable" }); return; }
      setState({ loading: false, data: await r.json(), error: null });
    } catch {
      setState({ loading: false, data: null, error: "unreachable" });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ⚠ Separate from load(): a failed notification fetch must never affect
  // whether the desk renders. The desk is the product; the bell is a nudge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await backendFetch("/api/thesis/notifications", "/api/thesis/notifications");
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (!cancelled) setNotes(Array.isArray(d?.notifications) ? d.notifications : []);
      } catch { /* no notifications is a fine outcome */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ⚠⚠ MARKED READ WHEN THE DROPDOWN OPENS, NOT WHEN THE LIST IS FETCHED. The
  // fetch happens on every mount, including mounts the user never looks at;
  // clearing on fetch is what the old read-and-delete did and is why a
  // background load could silently consume the whole list.
  // ⚠ Local state is updated optimistically so the dot clears immediately. The
  // POST failing costs nothing — the notifications are still there and the dot
  // returns on the next load.
  const markRead = useCallback(async () => {
    setNotes((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      const p = "/api/thesis/notifications/read";
      await backendFetch(p, p, { method: "POST" });
    } catch { /* the dot returning is an acceptable failure */ }
  }, []);

  const archive = async (id) => {
    setBusy(true);
    setActionError(null);
    try {
      const path = `/api/thesis/${id}/archive`;
      const r = await backendFetch(path, path, { method: "POST" });
      if (!r.ok) {
        // ⚠ Surface the server's own sentence. "Thesis not found, or already
        // archived." explains a no-op that otherwise reads as a dead button.
        let msg = "Could not archive the thesis. Try again.";
        try { const e = await r.json(); if (e?.detail) msg = String(e.detail); } catch { /* keep */ }
        setActionError(msg);
        return;
      }
      // ⚠⚠ RE-FETCH RATHER THAN ASSUME. Setting data:null locally would show the
      // builder even if the archive had silently not applied; asking
      // /api/thesis/me makes the server decide, and its 404 is what renders the
      // builder — the identical path a cold start takes.
      setEditing(false);
      await load();
      // ⚠⚠ THE BLANK SCREEN. s.main is the scroll container (overflowY: auto) and
      // the browser keeps its offset across the swap. The desk is far taller than
      // the builder, so a user who had scrolled ends up parked below the end of
      // the new, shorter content — looking at empty background, with the builder
      // rendered correctly above them. Nothing threw; there was nothing to see.
      requestAnimationFrame(() => {
        try { rootRef.current?.scrollIntoView({ block: "start" }); } catch { /* not fatal */ }
      });
    } catch {
      setActionError("Could not reach the platform.");
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return <div ref={rootRef} style={{ fontFamily: font, color: C.textDim, padding: 16 }}>
      Loading thesis…
    </div>;
  }

  // ⚠ An unreachable backend is NOT "no thesis". Dropping into the builder here
  // would invite a user to rebuild a thesis they already have.
  if (state.error) {
    return (
      <div style={{ fontFamily: font, color: C.text, padding: 16 }}>
        <div style={{ color: C.orange }}>Thesis Desk is unreachable right now.</div>
        <button style={{ ...s.btn(), minHeight: 44, marginTop: 10 }} onClick={load}>RETRY</button>
      </div>
    );
  }

  if (!state.data) {
    return (
      <div ref={rootRef} style={{ padding: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <NousIcon size={48} />
          <span style={{ fontFamily: font, fontSize: 16, fontWeight: 700, color: C.green }}>
            BUILD YOUR THESIS
          </span>
        </div>
        <BuilderBoundary>
          <ThesisBuilder onBuilt={load} />
        </BuilderBoundary>
        {/* ⚠ Below the builder, never in front of it. The user came here to
            build; history is reference material, not a step. */}
        <ThesisHistory />
      </div>
    );
  }

  const { thesis, signals = [], score = {}, windows = [] } = state.data;
  const label = score.label || "WATCHING";
  const color = SCORE_COLOR[label] || C.textDim;
  const counts = score.counts || { ALIGNS: 0, CHALLENGES: 0, NEUTRAL: 0 };
  const hasSignals = signals.length > 0;
  const tickers = Array.isArray(thesis.tickers) ? thesis.tickers : [];

  // per-ticker rollup
  const byTicker = tickers.map((t) => {
    const mine = signals.filter((sg) => sg.ticker === t.ticker);
    return {
      ...t,
      aligns: mine.filter((sg) => sg.thesis_alignment === "ALIGNS").length,
      challenges: mine.filter((sg) => sg.thesis_alignment === "CHALLENGES").length,
      last: mine[0]?.detected_at || null,
      // ⚠ mine[0] is the NEWEST: the endpoint returns signals ORDER BY
      // detected_at DESC, so the first match is the most recent one and no
      // sort is needed here.
      lastLabel: mine[0]?.signal_label || "",
      // ⚠ The signals themselves, already newest-first from the endpoint, so
      // level 3 of the accordion needs no second sort and cannot disagree with
      // the `last` timestamp shown on the anchor above it.
      signals: mine,
      n: mine.length,
    };
  }).sort((a, b) => b.n - a.n);

  return (
    <div ref={rootRef} style={{ fontFamily: font, color: C.text, fontSize: 13 }}>
      {/* ── header ── */}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <NousIcon size={48} />
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.green }}>{thesis.thesis_name}</div>
          <div style={{ color: C.textDim }}>
            {String(thesis.direction).toUpperCase()} · {String(thesis.style).toUpperCase()}
            {" · "}{String(thesis.timeframe).toUpperCase()}
          </div>
          <div style={{ color: C.textDim, fontSize: 10 }}>
            Created {etStamp(thesis.created_at)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {/* ⚠⚠ THIS BUTTON HAD NO onClick AT ALL — style, disabled and title,
              but no handler, so it was inert whether or not signals existed.
              ⚠ Still refused once signals exist, but as an explanation rather
              than a dead control: clicking now says why, and offers the archive
              that IS the way forward. hasSignals comes from data already on the
              client, so the check costs no request. */}
          <button style={{ ...s.btn(), minHeight: 44 }}
                  onClick={() => {
                    if (hasSignals) {
                      setEditErr("Your thesis has active signals. Archive and rebuild to make changes.");
                      return;
                    }
                    setEditErr(null);
                    setEditing(true);
                  }}
                  title={hasSignals
                    ? "Your thesis has active signals. Archive and rebuild to make changes."
                    : "Edit thesis"}>
            EDIT
          </button>
          <button style={{ ...s.btn("red"), minHeight: 44 }} disabled={busy}
                  onClick={() => archive(thesis.id)}>
            {busy ? "…" : "ARCHIVE"}
          </button>
          <button style={{ ...s.btn(), minHeight: 44 }}
                  onClick={() => { setShareQuote(null); setShareOpen(true); }}
                  title={thesis.is_public ? "Share your public ledger"
                                          : "Publish your ledger"}>
            {thesis.is_public ? "SHARE" : "PUBLISH"}
          </button>
          {/* ⚠ Costs a model call on the server, so it says what it is doing and
              refuses to be pressed twice while one is in flight. */}
          <button style={{ ...s.btn(), minHeight: 44,
                           display: "flex", alignItems: "center", gap: 6 }}
                  disabled={briefLoading}
                  onClick={requestBrief}
                  title="An intelligence brief on your thesis">
            {briefLoading && (
              <span style={{ animation: "qsPulse 1.5s ease-in-out infinite" }}>
                <NousIcon size={16} />
              </span>
            )}
            {briefLoading ? "PREPARING BRIEF\u2026" : "BRIEF ME"}
          </button>
          <NotificationBell
            items={notes} open={bellOpen}
            onToggle={() => {
              const next = !bellOpen;
              setBellOpen(next);
              if (next) markRead();
            }}
            onShare={(nt) => {
              setBellOpen(false);
              // ⚠ The triggering notification rides through to the card so the
              // signal it resolved can be marked VERIFIED WIN rather than
              // sitting anonymously among the other two.
              setShareSignal(nt || null);
              setShareQuote(null);
              setShareOpen(true);
            }} />
        </div>
      </div>

      {actionError && (
        <div style={{ color: C.red, fontSize: 12, marginTop: 8 }}>{actionError}</div>
      )}

      {/* ⚠ The refusal carries the way forward with it. A message that only says
          "no" leaves the user hunting for the button that does work. */}
      {editErr && (
        <div style={{ ...s.panel, padding: 12, marginTop: 10, lineHeight: 1.7 }}>
          <div style={{ color: C.orange, fontSize: 12 }}>{editErr}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button style={{ ...s.btn("red"), minHeight: 44 }} disabled={busy}
                    onClick={() => archive(thesis.id)}>
              {busy ? "…" : "ARCHIVE THESIS"}
            </button>
            <button style={{ ...s.btn(), minHeight: 44 }} onClick={() => setEditErr(null)}>
              DISMISS
            </button>
          </div>
        </div>
      )}

      {editing && (
        <div style={{ marginTop: 12 }}>
          <EditThesis
            thesis={thesis}
            onCancel={() => setEditing(false)}
            onSaved={(updated) => {
              setEditing(false);
              setState((st) => ({
                ...st,
                data: { ...st.data, thesis: { ...st.data.thesis, ...updated } },
              }));
            }}
          />
        </div>
      )}

      {/* ── score ── */}
      <div style={{ marginTop: 14 }}>
        <span style={{
          fontSize: 15, fontWeight: 700, color, border: `1px solid ${color}`,
          borderRadius: 3, padding: "4px 12px", letterSpacing: "0.08em",
        }}>
          {label}
        </span>
        <div style={{ color: C.textDim, marginTop: 6, fontSize: 12 }}>
          {label === "WATCHING"
            ? "No signals yet \u2014 the platform is watching your basket."
            : `${counts.ALIGNS} aligning · ${counts.CHALLENGES} challenging · ${counts.NEUTRAL} neutral`}
        </div>

        {/* ── ticker cards — the signal surface ──
            ⚠⚠ THE CARDS *ARE* THE FEED. There is no separate signal list any
            more. A flat feed of one card per signal ran to 141 rows on a live
            thesis and buried every section under it; a capped feed plus a
            sector accordion plus these cards was three surfaces answering the
            same question in three shapes. Each card is now the anchor for its
            own ticker's signals, so the basket is the index and there is
            nothing to scroll past to reach it.
            ⚠⚠ flexWrap, NEVER overflowX. Fifteen 150px cards in a horizontal
            scroller put ten of them off-screen behind a scrollbar that is easy
            to miss, which reads as "my tickers are gone" rather than "scroll
            sideways". Wrapping is the only layout where the whole basket is
            visible at once. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {byTicker.map((t) => {
            // ⚠ A quiet ticker has nothing to expand, so it is not made
            // tappable — a control that opens onto nothing reads as broken
            // rather than as empty. It stays VISIBLE though, dimmed: the thesis
            // basket is always shown in full, and dropping a ticker because it
            // is quiet would misstate what the user is holding.
            const canOpen = t.n > 0;
            const open = canOpen && openTickers.has(t.ticker);
            return (
              <div key={t.ticker} style={{
                ...s.panel, padding: 10, minWidth: 150, flexShrink: 0,
                // ⚠⚠ AN OPEN CARD TAKES THE WHOLE ROW. Signal rows inside a
                // 150px card on a wrapping flex line would either stretch that
                // line to the width of the longest label or force its siblings
                // to reflow around a column of text. Spanning keeps the list
                // visually attached to the card it belongs to and pushes the
                // rest of the basket cleanly below it.
                flexBasis: open ? "100%" : undefined,
                opacity: canOpen ? 1 : 0.55,
              }}>
                {/* ⚠⚠ THE HEADER CARRIES THE TOGGLE, NOT THE CARD. With the
                    handler on the card itself, every tap inside an open card —
                    including on the signal the reader is trying to read —
                    bubbles up and collapses it. Collapsed, the header IS the
                    whole card, so the tap target is unchanged. */}
                <div
                  onClick={canOpen ? () => setOpenTickers((prev) => {
                    // ⚠ A NEW Set every time. Mutating and returning the same
                    // reference leaves React comparing a value to itself and the
                    // card never re-renders.
                    const next = new Set(prev);
                    if (next.has(t.ticker)) next.delete(t.ticker);
                    else next.add(t.ticker);
                    return next;
                  }) : undefined}
                  style={{ cursor: canOpen ? "pointer" : "default",
                           minHeight: canOpen ? 44 : undefined }}
                >
                  <div style={{ fontWeight: 700, display: "flex", alignItems: "center",
                                justifyContent: "space-between", gap: 6 }}>
                    <span>{t.ticker}</span>
                    {/* ⚠ No chevron on a quiet card. The chevron is the promise
                        that there is something behind the tap. */}
                    {canOpen && (
                      <span style={{ color: C.textDim, fontSize: 11 }}>
                        {open ? "▲" : "▼"}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim, whiteSpace: "nowrap",
                                overflow: "hidden", textOverflow: "ellipsis",
                                maxWidth: open ? undefined : 130 }}>
                    {SECTOR_ICONS[t.sector] || ""} {t.name || t.sector || ""}
                  </div>
                  <div style={{ fontSize: 11, marginTop: 4 }}>
                    <span style={{ color: C.green }}>{t.aligns}</span>
                    <span style={{ color: C.textDim }}>{" aligning · "}</span>
                    <span style={{ color: C.red }}>{t.challenges}</span>
                    <span style={{ color: C.textDim }}>{" challenging"}</span>
                  </div>
                  {/* ⚠ Relative here, absolute on hover. The card answers "is
                      this fresh?"; the exact stamp stays in the title. */}
                  <div style={{ fontSize: 10, display: "flex", gap: 6,
                                alignItems: "baseline", flexWrap: "wrap" }}
                       title={t.last ? etStamp(t.last) : ""}>
                    {canOpen && (
                      <span style={{ color: C.green, fontWeight: 700 }}>
                        {`${t.n} signal${t.n === 1 ? "" : "s"}`}
                      </span>
                    )}
                    <span style={{ color: C.textDim }}>
                      {t.last ? ago(t.last) : "No signals yet"}
                    </span>
                  </div>
                </div>

                {open && (
                  <div style={{ marginTop: 8, paddingTop: 8,
                                borderTop: `1px solid ${C.border}` }}>
                    {/* ⚠ Already newest-first: the endpoint returns signals
                        ORDER BY detected_at DESC and byTicker preserves that
                        order, so a second sort here could only disagree with the
                        timestamp on the header above it. NO CAP — the card is
                        the container and the reader asked for this one ticker. */}
                    {t.signals.map((sg, i) => {
                      // ⚠ Reuses ALIGN_STYLE, the same map SignalRow uses, so an
                      // ALIGNS here cannot render a different colour from the
                      // identical signal on the public ledger.
                      const al = ALIGN_STYLE[String(sg.thesis_alignment || "")
                        .toUpperCase()] || ALIGN_STYLE.NEUTRAL;
                      // ⚠ The FIRST RESOLVED window, not all of them. `windows`
                      // is already narrowed to this thesis's own timeframe and
                      // every other outcome column is NULL by design; four
                      // repetitions of "pending" read as a measurement that came
                      // back empty rather than one that has not run yet.
                      const done = windows.find((w) => sg[`outcome_${w}`] != null);
                      const ok = done ? sg[`direction_correct_${done}`] : null;
                      const num = done ? Number(sg[`outcome_${done}`]) : null;
                      return (
                        <div key={sg.id || `${sg.ticker}-${sg.detected_at}`}
                             style={{
                               display: "flex", gap: 8, alignItems: "baseline",
                               flexWrap: "wrap", padding: "6px 0",
                               borderTop: i === 0 ? undefined : `1px solid ${C.border}`,
                             }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, color: al.color,
                            border: `1px solid ${al.color}`, borderRadius: 10,
                            padding: "1px 7px", whiteSpace: "nowrap",
                          }}>
                            {al.label}
                          </span>
                          <span style={{ fontSize: 12, color: C.text }}>
                            {sg.signal_label}
                          </span>
                          <span style={{ fontSize: 10, color: C.textDim }}>
                            {etStamp(sg.detected_at)}
                            {sg.price_at_detection != null
                              ? ` · $${Number(sg.price_at_detection).toFixed(2)}`
                              : ""}
                          </span>
                          <span style={{ fontSize: 10, marginLeft: "auto",
                                         color: done ? (ok ? C.green : C.red) : C.textDim }}>
                            {done
                              ? `${done}: ${num > 0 ? "+" : ""}${num.toFixed(1)}% ${ok ? "✓" : "✗"}`
                              : "pending"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {briefError && (
        <div style={{ color: C.orange, fontSize: 12, marginTop: 10 }}>{briefError}</div>
      )}

      {briefData && (
        <BriefPanel
          data={briefData}
          onShare={() => {
            setShareQuote(briefData.pull_quote || null);
            setShareSignal(null);
            setShareOpen(true);
          }}
        />
      )}

      {/* ── thesis note ── */}
      <NoteSection
        thesis={thesis}
        onSaved={(updated) => {
          // ⚠ No payload means the server refused (the note was sealed while the
          // editor was open). Reload rather than merge — the server state is now
          // the truth and the desk has to show the seal.
          if (!updated) { load(); return; }
          // ⚠ Merge, never replace: the note response is a thesis ROW and carries
          // no signals or score, so assigning it wholesale would blank the feed
          // until the next load.
          setState((st) => ({
            ...st,
            data: { ...st.data, thesis: { ...st.data.thesis, ...updated } },
          }));
        }}
      />

      {shareOpen && (
        <ShareModal
          thesis={thesis}
          highlight={shareSignal}
          pullQuote={shareQuote}
          onClose={() => { setShareOpen(false); setShareSignal(null); setShareQuote(null); }}
          onPublished={(updated) => setState((st) => ({
            ...st, data: { ...st.data, thesis: { ...st.data.thesis, ...updated } },
          }))}
        />
      )}

      {/* ⚠ Stated plainly rather than left as a row of "pending" the user has
          to interpret. A QUARTERS thesis carries a 90-day horizon and is scored
          weekly, so its position is genuinely open for a quarter — this says so
          instead of showing a dash the reader has to guess at.
          ⚠ This used to read "alpha_ledger measures to 21 days", which described
          a ceiling that no longer exists: scoring now marks each position against
          its own thesis horizon (DAYS 3d / WEEKS 7d / MONTHS 30d / QUARTERS 90d)
          rather than intersecting with a fixed set of ledger columns. */}
      {thesis.timeframe === "quarters" && (
        <div style={{ ...s.panel, padding: 10, marginTop: 10,
                      color: C.textDim, fontSize: 11, lineHeight: 1.6 }}>
          Quarter-length outcome windows require 30-90 days to resolve.
        </div>
      )}

      <Disclaimer />
    </div>
  );
}

export default ThesisDesk;
